import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarCheck, Mail, Send } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
};

const inputClass =
  "w-full rounded-xl border border-jade/20 bg-paper/60 px-4 py-3 text-sm text-ink placeholder:text-ink2/40 transition-colors duration-300";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("idle");

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, form, { timeout: 30000 });
      setStatus("sent");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      setStatus("error");
      console.error("Contact send failed:", err?.response?.data?.detail || err);
    }
  };

  return (
    <section
      id="contact"
      className="relative overflow-hidden border-t border-jade/10 bg-paper2/70 py-24 lg:py-32"
    >
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 lg:grid-cols-2 lg:px-10">
        <motion.div {...fadeUp} transition={{ duration: 0.7 }}>
          <p className="text-xs font-medium uppercase tracking-[0.3em] text-jade">
            Contact
          </p>
          <h2
            className="mt-4 font-display text-3xl font-light leading-tight tracking-tight text-ink sm:text-4xl lg:text-5xl"
            data-testid="contact-heading"
          >
            Tell me about <em className="italic text-jade">your project</em>.
          </h2>
          <p className="mt-6 max-w-md leading-relaxed text-ink2">
            Whether you need a first website, an AI tool or help getting found
            on Google, send a few lines and I'll get back to you with a clear
            idea of how I can help.
          </p>

          <div className="mt-10 space-y-4">
            <div className="flex flex-wrap gap-3">
              <Link
                to="/book"
                data-testid="book-call-button"
                className="inline-flex items-center gap-3 rounded-full bg-jade px-7 py-3.5 text-sm font-medium text-paper shadow-ambient transition-colors duration-300 hover:bg-ink"
              >
                <CalendarCheck className="h-4 w-4" />
                Book an intro call
              </Link>
              <Link
                to="/pay"
                data-testid="pay-invoice-button"
                className="inline-flex items-center gap-3 rounded-full border border-jade/30 px-7 py-3.5 text-sm font-medium text-jade transition-colors duration-300 hover:bg-jade/5"
              >
                Pay an invoice
              </Link>
            </div>
            <p className="flex items-center gap-2 text-sm text-ink2/80">
              <Mail className="h-4 w-4 text-jade" />
              Prefer email?
              <a
                href="mailto:groupvagus@gmail.com"
                data-testid="contact-email-link"
                className="font-medium text-jade underline-offset-4 transition-colors duration-300 hover:underline"
              >
                groupvagus@gmail.com
              </a>
            </p>
          </div>
        </motion.div>

        <motion.form
          {...fadeUp}
          transition={{ duration: 0.7, delay: 0.12 }}
          onSubmit={submit}
          className="rounded-2xl bg-white p-8 shadow-ambient lg:p-10"
          data-testid="contact-form"
        >
          <div className="space-y-5">
            <div>
              <label
                htmlFor="contact-name"
                className="mb-2 block text-xs font-medium uppercase tracking-[0.15em] text-ink2"
              >
                Name
              </label>
              <input
                id="contact-name"
                data-testid="contact-name-input"
                className={inputClass}
                value={form.name}
                onChange={update("name")}
                placeholder="Your name"
                required
                maxLength={100}
              />
            </div>
            <div>
              <label
                htmlFor="contact-email"
                className="mb-2 block text-xs font-medium uppercase tracking-[0.15em] text-ink2"
              >
                Email
              </label>
              <input
                id="contact-email"
                type="email"
                data-testid="contact-email-input"
                className={inputClass}
                value={form.email}
                onChange={update("email")}
                placeholder="you@example.com"
                required
                maxLength={200}
              />
            </div>
            <div>
              <label
                htmlFor="contact-message"
                className="mb-2 block text-xs font-medium uppercase tracking-[0.15em] text-ink2"
              >
                What do you need?
              </label>
              <textarea
                id="contact-message"
                data-testid="contact-message-input"
                className={`${inputClass} min-h-[130px] resize-y`}
                value={form.message}
                onChange={update("message")}
                placeholder="A few lines about your business and your website..."
                required
                maxLength={5000}
              />
            </div>
            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={status === "sending"}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-jade px-8 py-3.5 text-sm font-medium text-paper transition-colors duration-300 hover:bg-ink disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Send className="h-4 w-4" />
              {status === "sending" ? "Sending…" : "Send message"}
            </button>
            <p
              role="status"
              data-testid="contact-form-status"
              className={`text-center text-sm ${
                status === "sent"
                  ? "text-jade"
                  : status === "error"
                  ? "text-red-700"
                  : "text-ink2/50"
              }`}
            >
              {status === "sent"
                ? "Thanks, your message is on its way. I'll be in touch soon."
                : status === "error"
                ? "Something went wrong sending that. Please email groupvagus@gmail.com directly."
                : "I reply to every message personally."}
            </p>
          </div>
        </motion.form>
      </div>
    </section>
  );
};

export default Contact;